import React, { FC, useEffect, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { ResizeObserver } from '@juggle/resize-observer';
import { useClientGalleryServiceContext } from 'ts/common/contexts/ClientGalleryServiceContext';
import { useCoverSlideshowContext } from 'ts/common/contexts/CoverSlideshowContext';
import type { ICoverPhotoBase, IFocalPointImageValues } from 'ts/common/types';
import { computeFocalPointImageValues } from 'ts/common/utils';
import CoverSlideshowBackground from './CoverSlideshowBackground';

interface Props {
    /** The size and focal point of the image to be displayed */
    coverPhoto: ICoverPhotoBase;
    /** The recommended image url to use given the current window size */
    coverPhotoUrl: Nullable<string>;
    /** Label text for this image, for accessibility purposes. Should be provided but is optional for legacy support */
    label?: string;
}

const CoverPhoto: FC<Props> = ({ coverPhoto, coverPhotoUrl, label }) => {
    const imageRef = useRef<HTMLDivElement>(null);
    const [imageBounds, setImageBounds] = useState<IFocalPointImageValues>();
    const [isReadyToLoadImage, setIsReadyToLoadImage] = useState(false);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            // Using requestAnimationFrame prevents "Error: ResizeObserver loop completed with undelivered notifications."
            window.requestAnimationFrame(() => {
                if (imageRef.current) {
                    const nextImageBounds = computeFocalPointImageValues(
                        imageRef.current.offsetWidth,
                        imageRef.current.offsetHeight,
                        coverPhoto.width,
                        coverPhoto.height,
                        coverPhoto.focalPoint
                    );
                    setImageBounds(nextImageBounds);
                }
            });
        });

        if (imageRef.current) {
            resizeObserver.observe(imageRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [coverPhoto]);

    // Prevent loading the image until it has entered the viewport once:
    useEffect(() => {
        const intersectionObserver = new IntersectionObserver(([{ isIntersecting }]) => {
            // Check to see if the image is within the viewport now:
            if (isIntersecting) {
                setIsReadyToLoadImage(true); // Mark the image as ready to load, since it has entered the viewport.
                intersectionObserver.disconnect(); // Disconnect the IntersectionObserver for efficiency, since we're done with it.
            }
        });

        if (imageRef.current) {
            intersectionObserver.observe(imageRef.current);
        }

        return () => {
            intersectionObserver.disconnect();
        };
    }, []);

    // Fallback: if we have computed bounds and a URL, eagerly allow load.
    // This ensures previews inside scaled/hidden containers render without requiring a window resize.
    useEffect(() => {
        if (!isReadyToLoadImage && imageBounds && coverPhotoUrl) {
            setIsReadyToLoadImage(true);
        }
    }, [isReadyToLoadImage, imageBounds, coverPhotoUrl]);

    // Only present once a cover slideshow has been fully loaded by whichever surface is rendering
    // this — the client gallery's own gates above the intro page's loading state, or the Studio
    // branding preview's (see CoverSlideshowContext). Absent for a plain photo cover and for a
    // visitor who prefers reduced motion, in which case only the static poster below is rendered.
    const coverSlideshow = useCoverSlideshowContext();
    // Only the client gallery has an event bus; the Studio branding preview plays silently without
    // one, so this is passed along as-is rather than gating playback.
    const clientGalleryServices = useClientGalleryServiceContext();

    return (
        // `position: relative` is only here to be the containing block for the absolutely
        // positioned slideshow layer below — it has no visual offset of its own. But every
        // intro layout stacks its title/button/gradient overlay as `position: absolute`
        // siblings of this component, and CSS paints ALL positioned (or stacking-context-
        // forming) siblings sharing a stacking context in DOM order — *after* ordinary
        // non-positioned content — regardless of z-index:auto. Before this component needed
        // `position: relative` at all, it was plain/non-positioned and so always painted
        // beneath those overlays for free. `zIndex={-1}` restores that: it gives this box its
        // own (fully self-contained) stacking context at a *negative* stack level, which CSS
        // paints before (behind) the layout's absolutely positioned overlay siblings — without
        // touching any of the ~19 layout files that compose this component.
        <Box position="relative" zIndex={-1} width="100%" height="100%">
            <Box
                ref={imageRef}
                role="img"
                aria-label={label}
                data-testid="gallery-cover-photo"
                backgroundImage={
                    imageBounds && isReadyToLoadImage && coverPhotoUrl
                        ? `url(${coverPhotoUrl})`
                        : 'none'
                }
                backgroundPosition={
                    imageBounds && `${imageBounds.offsetX}px ${imageBounds.offsetY}px`
                }
                backgroundSize={imageBounds && `${imageBounds.width}px ${imageBounds.height}px`}
                width="100%"
                height="100%"
            />
            {coverSlideshow && (
                <CoverSlideshowBackground
                    coverSlideshow={coverSlideshow}
                    spEventBus={clientGalleryServices?.spEventBus}
                />
            )}
        </Box>
    );
};

export default CoverPhoto;
